import React, { useState, useEffect } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, EffectFade } from "swiper/modules";
import { FaTimes, FaChevronLeft, FaChevronRight, FaImages, FaCalendarAlt } from "react-icons/fa";
import axios from "axios";
import { useTranslation } from "react-i18next";
import line from "../Assests/line.png";
import BASE_URL from "../Components/constant";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import "./Events.css";

const Event = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/api/v1/events-gallery`);
        if (response.data?.success && response.data?.data) {
          const shuffled = [...response.data.data];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          setEvents(shuffled.slice(0, 16));
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const openImageModal = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    if (selectedIndex < events.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedImage(events[selectedIndex + 1]);
    }
  };

  const prevImage = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedImage(events[selectedIndex - 1]);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showModal) return;
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [showModal, selectedIndex]);

  const getSlides = () => {
    const slides = [];
    for (let i = 0; i < events.length; i += 4) {
      slides.push(events.slice(i, i + 4));
    }
    return slides;
  };

  const slides = getSlides();

  if (loading) {
    return (
      <div className="evnt-loading">
        <Container>
          <div className="evnt-skeleton">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="evnt-skeleton-card">
                <div className="evnt-skeleton-img shimmer"></div>
              </div>
            ))}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="evnt-page">
      <Container fluid className="evnt-container">
        {/* Luxury Header */}
        <div className="evnt-header">
          <div className="evnt-header-badge">
            <FaCalendarAlt className="evnt-badge-icon" />
            <span>Memories</span>
          </div>
          <h1 className="evnt-title">
            Events <span className="evnt-title-highlight">Gallery</span>
          </h1>
          <div className="evnt-title-decoration">
            <div className="evnt-decoration-line"></div>
            <img src={line} alt="decoration" className="evnt-decoration-img" />
            <div className="evnt-decoration-line"></div>
          </div>
          <p className="evnt-subtitle">
            Capturing beautiful moments from our events and celebrations
          </p>
        </div>

        {/* Luxury Gallery Grid */}
        {events.length > 0 ? (
          <div className="evnt-gallery">
<Swiper
  modules={[Autoplay]}   // ❌ remove Pagination & Navigation
  spaceBetween={20}
  slidesPerView={4}
  loop={true}
  autoplay={{
    delay: 2000,
    disableOnInteraction: false,
  }}
  breakpoints={{
    320: { slidesPerView: 1 },
    576: { slidesPerView: 2 },
    768: { slidesPerView: 3 },
    992: { slidesPerView: 4 },
  }}
  className="evnt-swiper"
>
  {events.slice(0,20).map((img, idx) => (
    <SwiperSlide key={idx}>
      <div
        className="evnt-gallery-card"
        onClick={() => openImageModal(img, idx)}
      >
        <div className="evnt-card-inner">
          <img
            src={img.mobile_image || img.desktop_image}
            alt={`Event ${idx + 1}`}
            className="evnt-card-img"
          />

          <div className="evnt-card-overlay">
            <div className="evnt-card-icon">
              <FaImages />
            </div>
            <span className="evnt-card-number">#{idx + 1}</span>
          </div>
        </div>
      </div>
    </SwiperSlide>
  ))}
</Swiper>
          </div>
        ) : (
          <div className="evnt-empty">
            <FaImages className="evnt-empty-icon" />
            <p>No events found</p>
          </div>
        )}
      </Container>

      {/* Luxury Lightbox Modal */}
      <Modal show={showModal} onHide={closeModal} fullscreen={true} centered className="evnt-modal">
        <Modal.Body className="evnt-modal-body">
          <button className="evnt-modal-close" onClick={closeModal}>
            <FaTimes />
          </button>
          
          {selectedImage && (
            <div className="evnt-modal-container">
              <button 
                className="evnt-modal-nav evnt-modal-prev" 
                onClick={prevImage} 
                disabled={selectedIndex === 0}
              >
                <FaChevronLeft />
              </button>
              
              <div className="evnt-modal-image-wrap">
                <img
                  src={selectedImage.desktop_image || selectedImage.mobile_image}
                  alt="Event preview"
                  className="evnt-modal-image"
                />
                <div className="evnt-modal-info">
                  <span className="evnt-modal-counter">
                    {String(selectedIndex + 1).padStart(2, '0')} / {String(events.length).padStart(2, '0')}
                  </span>
                </div>
              </div>
              
              <button 
                className="evnt-modal-nav evnt-modal-next" 
                onClick={nextImage} 
                disabled={selectedIndex === events.length - 1}
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Event;